import { standaloneRentersStates } from '@ourbranch/lookups';
import type { DynamoDbCarDetails, DynamoDbOffer, DynamoDbPolicy } from '@ourbranch/schema-types/dynamodb';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState, formatAddress, getPrimarySegment, normalizeAddressForComparison } from './lib/helpers';

/**
 * Address-mismatch verification (#36106).
 *
 * Replaces the two legacy internal tasks `Primary Residence Verification` (Home/Condo:
 * mailing ≠ property) and `Garage/Mailing Address Mismatch` (Auto: mailing ≠ property or
 * a car garaged elsewhere). Both fired on the same mailing-vs-property difference on a
 * bundle, and both compared raw text, so ZIP+4, "Ln." vs "Lane" and "N" vs "North" were
 * false positives (~55% of firings).
 *
 * One task per bind, placed by which policies the bind ACTUALLY creates (mirrors
 * `bind-requests-to-accounts/src/core/build-policies/policy-type-configs.ts`):
 * - A residence policy (Home, Condo or Renters) is bound for H (unless `noBindHome`), C, and
 *   R — but for an AR offer only in `standaloneRentersStates`; elsewhere renters is folded
 *   into the single auto policy (`includeRenters`) and no renters policy exists.
 * - When a residence policy is bound it hosts the task and lists the bundled cars garaged
 *   elsewhere, read from `offer.quote.cars` (`generate-policy-tasks` processes each bound
 *   policy in its own SQS message, so the auto policy may not exist yet) — only if the auto
 *   policy is actually bound too (`noBindAuto` not set).
 * - Otherwise the auto policy keeps the task with its own cars (monoline A, AR outside the
 *   standalone-renters states, HA with `noBindHome`).
 *
 * Addresses are compared with `normalizeAddressForComparison` (case, punctuation, USPS
 * suffix/directional abbreviations, unit designators and ZIP+4 ignored). The car-level
 * `isGarageAddressRuleExceptionApproved` exception is still honoured.
 */

const RESIDENCE_POLICY_TYPES = ['H', 'C', 'R'];

/** Letters in `offer.quote.selectedOption` ('H', 'A', 'R', 'C' combos such as 'HA', 'AR', 'CA'). */
const offerIncludes = (selectedOption: string | null | undefined, policyType: string): boolean =>
  (selectedOption || '').toUpperCase().includes(policyType);

/**
 * The option the bind actually used. bind-requests-to-accounts resolves `submitted.optionId`
 * against `offer.options` and keys `policyTypeConfigs` by THAT option's `type`, persisting its id
 * as `policy.optionid` — `quote.selectedOption` is only what the quote last had selected and can
 * disagree (adversarial review on #36174). Fall back to it when the option can't be resolved
 * (no offer, or an imported policy with no optionid).
 */
const boundOptionType = (policy: DynamoDbPolicy, offer: DynamoDbOffer | null): string | null | undefined => {
  const boundOption = policy.optionid ? offer?.options?.find((option) => option?.id === policy.optionid) : undefined;
  return boundOption?.type || offer?.quote?.selectedOption;
};

/**
 * Which policies this offer's bind creates. Same rules as `policy-type-configs.ts` +
 * `build-policies/index.ts` in bind-requests-to-accounts: home needs `!noBindHome`, auto needs
 * `!noBindAuto`, and an AR offer only gets a separate renters policy in standaloneRentersStates.
 */
const boundPolicies = (policy: DynamoDbPolicy, offer: DynamoDbOffer | null, fallbackState: string) => {
  const quote = offer?.quote;
  const selectedOption = boundOptionType(policy, offer);
  const state = quote?.correctedAddress?.state || fallbackState;
  const includesAuto = offerIncludes(selectedOption, 'A');
  const isStandaloneRentersState = !!(standaloneRentersStates as Record<string, boolean | undefined>)[state];
  const residenceBound =
    (offerIncludes(selectedOption, 'H') && !quote?.noBindHome) ||
    offerIncludes(selectedOption, 'C') ||
    (offerIncludes(selectedOption, 'R') && (!includesAuto || isStandaloneRentersState));
  const autoBound = includesAuto && !quote?.noBindAuto;
  return { residenceBound, autoBound };
};

const describeCar = (car: DynamoDbCarDetails): string =>
  `${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim() || 'Vehicle';

export const addressMismatchVerification = defineInternalTask({
  taskName: internalTasks.AddressMismatchVerification,
  config: {
    staffDisplayName: 'Mailing/garaging address does not match property: verification needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H', 'C', 'R', 'A'],
    enabled: true,
    trigger: ({ policy, offer }) => {
      const isResidencePolicy = RESIDENCE_POLICY_TYPES.includes(policy.policyType);
      const { residenceBound, autoBound } = boundPolicies(policy, offer, policy.state);

      // A bound residence policy owns the task; the auto policy only keeps it when no
      // residence policy exists to host it (monoline A, AR outside standaloneRentersStates,
      // HA with noBindHome).
      if (!isResidencePolicy && residenceBound) {
        return null;
      }

      const segment = getPrimarySegment(policy);
      const propertyAddress = segment?.correctedAddress;
      if (!propertyAddress) {
        return null;
      }
      const propertyKey = normalizeAddressForComparison(propertyAddress);
      if (!propertyKey) {
        return null;
      }

      const mailingAddress = segment?.global?.mailingAddress;
      const mailingKey = normalizeAddressForComparison(mailingAddress);
      const mailingMismatch = !!mailingKey && mailingKey !== propertyKey;

      // Residence policy: the bundled auto's cars live on the offer (only when that auto policy
      // is actually bound). Auto policy: its own cars.
      const bundledAuto = isResidencePolicy && autoBound;
      const carSource = isResidencePolicy ? (bundledAuto ? offer?.quote?.cars : []) : segment?.cars;
      const cars = (carSource || []).filter((c): c is DynamoDbCarDetails => !!c);

      const vehicleAddresses: string[] = [];
      for (const car of cars) {
        if (car.isGarageAddressRuleExceptionApproved) {
          continue;
        }
        const garageKey = normalizeAddressForComparison(car.garageLocation);
        if (garageKey && garageKey !== propertyKey) {
          vehicleAddresses.push(`${describeCar(car)} - Garaged at: ${formatAddress(car.garageLocation)}`);
        }
      }

      if (!mailingMismatch && vehicleAddresses.length === 0) {
        return null;
      }

      return {
        data: {
          propertyAddress: formatAddress(propertyAddress),
          mailingAddress: formatAddress(mailingAddress),
          mailingMismatch,
          vehicleAddresses,
          bundledAuto
        },
        bindState: baseBindState(policy)
      };
    },
    description: ({ policyId, data, bindState }) => {
      const lines = [
        `${policyId}: the mailing address${data.vehicleAddresses.length ? ' and/or a vehicle garaging address' : ''} does not match the insured property address.`,
        'Addresses were compared ignoring case, punctuation, USPS street-suffix and directional abbreviations (Lane/Ln, North/N), unit designators (Apt/Unit/#/Ste) and ZIP+4 — so this is a real difference in street, unit, city, state or 5-digit ZIP.',
        '',
        `Property address: ${data.propertyAddress || 'N/A'}`,
        `Mailing address: ${data.mailingAddress || 'N/A'} (${data.mailingMismatch ? 'DOES NOT MATCH' : 'matches'})`
      ];
      if (data.vehicleAddresses.length) {
        lines.push(
          '',
          `Vehicles garaged somewhere other than the property${data.bundledAuto ? ' (bundled auto policy)' : ''}:`,
          ...data.vehicleAddresses
        );
      }
      lines.push(
        '',
        `Policy state: ${bindState.state}`,
        'If the property is a secondary residence, or a vehicle is garaged elsewhere without an approved exception, take appropriate underwriting action.'
      );
      return lines.join('\n');
    }
  }
});
